import { useMemo } from "react"
import { router, usePage } from "@inertiajs/react"
import {
  renderQueryString,
  unstable_createAdapterProvider as createAdapterProvider,
  type unstable_AdapterInterface as AdapterInterface,
} from "nuqs/adapters/custom"

/**
 * nuqs, driven by Inertia's router instead of the History API.
 *
 * `.claude/rules/react.md` §3 and `.claude/rules/inertia.md` §10 both mandate
 * nuqs for filter state, and `nuqs` has been in `package.json` with zero
 * importers since it was added. This is the first use, and it is deliberately
 * the smallest possible one: a single enum param with a whitelist.
 *
 * The stock `nuqs/adapters/react` cannot be used here. It updates the URL with
 * `history.replaceState`, which Inertia never observes — the chip would repaint,
 * the address bar would change, and the figures would stay exactly as they were,
 * because the period is resolved server-side by `Staff::ReportPeriod`. Every
 * report figure on this screen is a server figure; a URL state that does not
 * reach the server is a filter that does not filter.
 *
 * So the adapter reads the query string off `usePage().url` (Inertia's own idea
 * of where it is, which stays correct through back/forward and through partial
 * reloads) and writes it with `router.get`. The write is `preserveState` so the
 * page does not remount under the chip that was just tapped, and `preserveScroll`
 * so an owner comparing two periods halfway down the mix table is not thrown
 * back to the top.
 */

function useInertiaAdapter(_watchKeys: string[]): AdapterInterface {
  const { url } = usePage()

  const searchParams = useMemo(() => new URLSearchParams(url.split("?")[1] ?? ""), [url])

  return {
    searchParams,
    updateUrl(search, options) {
      const path = url.split("?")[0]

      router.get(
        `${path}${renderQueryString(search)}`,
        {},
        {
          preserveState: true,
          preserveScroll: !options.scroll,
          replace: options.history !== "push",
        }
      )
    },
  }
}

export const NuqsInertiaAdapter = createAdapterProvider(useInertiaAdapter)
